import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { SEO } from "@/components/SEO";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import AuditLanding from "@/components/audit/AuditLanding";
import AuditStep1 from "@/components/audit/AuditStep1";
import AuditStep2 from "@/components/audit/AuditStep2";
import AuditStep3 from "@/components/audit/AuditStep3";
import AuditResults from "@/components/audit/AuditResults";
import ProgressIndicator from "@/components/audit/ProgressIndicator";
import ExitConfirmDialog from "@/components/audit/ExitConfirmDialog";

export interface AuditFormData {
  // Step 1
  industry: string;
  company_size: string;
  monthly_leads: string;
  // Step 2
  process_standardization: number;
  tools: string[];
  manual_hours: string;
  // Step 3
  primary_outcome: string;
  ai_comfort: number;
  budget_timeframe: string;
  // Contact
  name: string;
  email: string;
  phone: string;
  consent_given: boolean;
}

export interface ScoringResult {
  score: number;
  tier: string;
  topOpportunities: string[];
  recommendation: string;
}

export default function AuditFlow() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0); // 0 = landing, 1-3 = questions, 4 = results
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<AuditFormData>>({});
  const [scoringResult, setScoringResult] = useState<ScoringResult | null>(null);

  const handleStart = () => {
    setStep(1);
    // Track analytics
    if (typeof window !== "undefined" && (window as any).gtag) {
      (window as any).gtag("event", "audit_start", {
        event_category: "engagement",
        event_label: "AI Transformation Audit",
      });
    }
  };

  const handleNext = (data: Partial<AuditFormData>) => {
    setFormData({ ...formData, ...data });
    setStep(step + 1);
    
    // Track analytics
    if (typeof window !== "undefined" && (window as any).gtag) {
      (window as any).gtag("event", "audit_step_next", {
        event_category: "engagement",
        event_label: `Step ${step}`,
      });
    }
  };

  const handleBack = () => {
    // Show confirmation dialog if user has data and tries to go back
    if (step > 1 && Object.keys(formData).length > 0) {
      setPendingNavigation('back');
      setShowExitDialog(true);
    } else if (step > 1) {
      setStep(step - 1);
    } else {
      navigate("/");
    }
  };

  const confirmExit = () => {
    setShowExitDialog(false);
    if (pendingNavigation === 'home') {
      navigate('/');
    } else if (pendingNavigation === 'back') {
      setStep(step - 1);
    }
    setPendingNavigation(null);
  };

  const updateFormData = (data: Partial<AuditFormData>) => {
    setFormData({ ...formData, ...data });
  };

  const handleResults = (result: ScoringResult) => {
    setScoringResult(result);
    setStep(4);
    
    // Track analytics
    if (typeof window !== "undefined" && (window as any).gtag) {
      (window as any).gtag("event", "audit_complete", {
        event_category: "conversion",
        event_label: `Tier: ${result.tier}`,
        value: result.score,
      });
    }
  };

  return (
    <div className="bg-deep-navy text-foreground min-h-screen">
      <SEO 
        title="Free AI Transformation Audit — ArqeraAI | Discover Your AI Opportunities"
        description="Take our free 2-minute AI Transformation Audit to discover your highest-ROI AI opportunities. Get personalized recommendations for your business."
        canonical="https://arqeraai.com/ai-transformation-audit"
      />
      <Navigation activeSection="audit" />
      
      <main className="pt-20 min-h-screen pb-20">
        {/* Progress Indicator - Only show during steps 1-3 */}
        {step >= 1 && step <= 3 && (
          <div className="max-w-7xl mx-auto px-6 pt-8">
            <ProgressIndicator currentStep={step} totalSteps={3} />
          </div>
        )}
        
        {step === 0 && <AuditLanding onStart={handleStart} />}
        
        {step === 1 && (
          <AuditStep1
            data={formData}
            onNext={handleNext}
            onBack={handleBack}
          />
        )}
        
        {step === 2 && (
          <AuditStep2
            data={formData}
            onNext={handleNext}
            onBack={handleBack}
          />
        )}
        
        {step === 3 && (
          <AuditStep3
            data={formData}
            onNext={handleNext}
            onBack={handleBack}
            onResults={handleResults}
          />
        )}
        
        {step === 4 && scoringResult && (
          <AuditResults
            result={scoringResult}
            formData={formData as AuditFormData}
          />
        )}
      </main>
      
      <Footer />
      
      {/* Exit Confirmation Dialog */}
      <ExitConfirmDialog
        open={showExitDialog}
        onOpenChange={setShowExitDialog}
        onConfirm={confirmExit}
      />
    </div>
  );
}